import { useEffect, useRef } from "react";
import { useAdminModelDetail } from "../hooks/useApi";
import {
  CAP_LIST,
  GEN_PARAM_LABELS,
  cap,
  fmtCtx,
  fmtUsd,
  modFa,
  type GenerationParamSpec,
  type ModelDetail,
} from "./ModelDetailDrawer";

interface AdminModelDetailDrawerProps {
  selectedModel: Record<string, unknown> | null;
  onClose: () => void;
}

const INFO_TIPS: Record<string, string> = {
  ctx: "حداکثر تعداد توکن‌هایی که مدل در یک درخواست می‌تواند به‌عنوان ورودی دریافت کند (حافظهٔ مدل). درخواست‌های طولانی‌تر از این مقدار رد می‌شوند.",
  mct: "سقف تعداد توکن‌های خروجی که مدل در هر پاسخ می‌تواند تولید کند؛ با پارامتر max_tokens قابل تنظیم و محدودکردن هزینه است.",
};

function YesNo(v: boolean | null | undefined): string {
  if (v === true) return "بله";
  if (v === false) return "خیر";
  return "—";
}

function ChipList({ items }: { items: string[] }) {
  if (!items.length) return <span style={{ color: "var(--admin-text-tertiary)" }}>—</span>;
  return (
    <span className="flex flex-wrap gap-1.5 items-start">
      {items.map((it) => (
        <code
          key={it}
          dir="ltr"
          className="font-mono"
          style={{
            fontSize: 11,
            color: "var(--admin-text-secondary)",
            background: "var(--admin-bg-hover)",
            border: "1px solid var(--admin-border)",
            borderRadius: 6,
            padding: "2px 8px",
          }}
        >
          {it}
        </code>
      ))}
    </span>
  );
}

function InfoTip({ text }: { text: string }) {
  return (
    <span className="relative inline-flex group shrink-0">
      <span
        className="inline-flex items-center justify-center rounded-full cursor-help select-none"
        style={{
          width: 16,
          height: 16,
          border: "1px solid var(--admin-border)",
          fontSize: 9,
          fontWeight: 700,
          lineHeight: 1,
          color: "var(--admin-text-tertiary)",
        }}
      >
        i
      </span>
      <span
        className="absolute right-0 top-full mt-1.5 hidden group-hover:block z-30 px-3 py-2 pointer-events-none"
        style={{
          width: 240,
          borderRadius: 10,
          fontSize: 11,
          lineHeight: 1.7,
          whiteSpace: "normal",
          background: "rgba(26,26,26,0.92)",
          color: "#fff",
          boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
        }}
      >
        {text}
      </span>
    </span>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="font-bold mb-3" style={{ fontSize: 15, color: "var(--admin-text-primary)" }}>
      {children}
    </h4>
  );
}

function MetaRow({
  label,
  info,
  children,
}: {
  label: React.ReactNode;
  info?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="flex items-start justify-between gap-4 py-2.5 last:border-0"
      style={{ borderBottom: "1px solid var(--admin-border-light)" }}
    >
      <span className="flex items-center gap-1.5 shrink-0" style={{ fontSize: 13, color: "var(--admin-text-secondary)" }}>
        {label}
        {info && <InfoTip text={info} />}
      </span>
      <span className="min-w-0 break-words text-left" style={{ fontSize: 13, color: "var(--admin-text-primary)" }}>
        {children}
      </span>
    </div>
  );
}

// Persian labels for the Design Arena top-level arenas (models/builders/agents)
// shown in the بررسی فنی benchmark block.
const ADMIN_ARENA_LABELS: Record<string, string> = {
  models: "مدلها",
  builders: "سازندگان",
  agents: "عاملها",
};

// Artificial Analysis index rows shown as scalar scores in the بررسی فنی block.
const ADMIN_AA_INDEX_LABELS: Array<[string, string]> = [
  ["intelligence_index", "شاخص هوش"],
  ["coding_index", "شاخص کدنویسی"],
  ["agentic_index", "شاخص عاملی"],
];

function adminBmNum(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") return Number.isInteger(v) ? v.toLocaleString("en-US") : String(v);
  return String(v);
}

function adminBmPct(v: unknown): string {
  if (v === null || v === undefined) return "—";
  return `٪${typeof v === "number" && !Number.isInteger(v) ? v.toFixed(1) : String(v)}`;
}

function AdminBenchmarksBlock(benchmarks: Record<string, unknown> | null | undefined) {
  if (!benchmarks || typeof benchmarks !== "object") return null;

  const rawAA = benchmarks.artificial_analysis;
  const aa =
    rawAA && typeof rawAA === "object" && !Array.isArray(rawAA)
      ? (rawAA as Record<string, unknown>)
      : null;

  const designArena = Array.isArray(benchmarks.design_arena)
    ? benchmarks.design_arena.filter((e) => e && typeof e === "object")
    : [];

  const otherKeys = Object.keys(benchmarks).filter(
    (k) => k !== "design_arena" && k !== "artificial_analysis"
  );

  if (!designArena.length && !aa && !otherKeys.length) return null;

  const arenaGroups = new Map<string, Array<Record<string, unknown>>>();
  designArena.forEach((entry) => {
    const arena = String((entry as Record<string, unknown>).arena || "other");
    if (!arenaGroups.has(arena)) arenaGroups.set(arena, []);
    arenaGroups.get(arena)!.push(entry as Record<string, unknown>);
  });

  return (
    <div>
      <SectionTitle>بررسی فنی</SectionTitle>

      {aa && (
        <dl>
          {ADMIN_AA_INDEX_LABELS.map(([key, label]) => (
            <MetaRow key={key} label={label}>
              <span dir="ltr">{adminBmNum(aa[key])}</span>
            </MetaRow>
          ))}
        </dl>
      )}

      {[...arenaGroups.entries()].map(([arena, entries]) => (
        <div key={arena} className="mb-3">
          <h5 style={{ fontSize: 12, fontWeight: 600, color: "var(--admin-text-secondary)", marginBottom: 8 }}>
            Design Arena · {ADMIN_ARENA_LABELS[arena] || arena}
          </h5>
          <div style={{ borderRadius: 10, border: "1px solid var(--admin-border)", overflow: "hidden" }}>
            <div className="overflow-x-auto">
              <table className="w-full" style={{ fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "var(--admin-bg-surface)", color: "var(--admin-text-tertiary)", borderBottom: "1px solid var(--admin-border)" }}>
                    <th className="text-right font-medium px-4 py-2.5 whitespace-nowrap">رده</th>
                    <th className="text-center font-medium px-4 py-2.5 whitespace-nowrap">رتبه</th>
                    <th className="text-center font-medium px-4 py-2.5 whitespace-nowrap">ELO</th>
                    <th className="text-center font-medium px-4 py-2.5 whitespace-nowrap">نرخ برد</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid var(--admin-border-light)" }}>
                      <td className="px-4 py-2.5 font-mono text-left whitespace-nowrap" dir="ltr">
                        {String(e.category || "—")}
                      </td>
                      <td className="px-4 py-2.5 text-center font-mono" dir="ltr">
                        {e.rank != null ? `#${e.rank}` : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-center font-mono" dir="ltr">
                        {adminBmNum(e.elo)}
                      </td>
                      <td className="px-4 py-2.5 text-center font-mono" dir="ltr">
                        {adminBmPct(e.win_rate)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ))}

      {otherKeys.length > 0 && (
        <dl>
          {otherKeys.map((k) => (
            <MetaRow key={k} label={<span className="font-mono" dir="ltr">{k}</span>}>
              <span className="font-mono" dir="ltr" style={{ fontSize: 11, color: "var(--admin-text-tertiary)" }}>
                {typeof benchmarks[k] === "string" ? benchmarks[k] : JSON.stringify(benchmarks[k])}
              </span>
            </MetaRow>
          ))}
        </dl>
      )}
    </div>
  );
}

export default function AdminModelDetailDrawer({ selectedModel, onClose }: AdminModelDetailDrawerProps) {
  const backdropMouseDown = useRef(false);
  const modelId = selectedModel ? Number(selectedModel.id) : null;
  const { data: detail, isLoading, isError } = useAdminModelDetail(modelId);

  useEffect(() => {
    if (selectedModel) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [selectedModel]);

  if (!selectedModel) return null;

  const d = detail as ModelDetail | undefined;
  const basis = d?.price_basis || "per_1m";
  const scale = basis === "per_1m" ? 1e6 : 1;

  const marginFactor = (100 + (d?.platform_fee_percent ?? 0)) / 100;
  const margined = (v: number | null | undefined) => (v == null ? null : v * marginFactor);

  const inUsd = margined(d ? (d.pricing.prompt != null ? d.pricing.prompt * scale : null) : null);
  const outUsd = margined(
    d
      ? d.pricing.completion != null && d.pricing.completion > 0
        ? d.pricing.completion * scale
        : null
      : null
  );

  const endpoints: Array<[string, string]> = [
    ["resp", "/v1/responses"],
    ["msg", "/v1/messages"],
  ];
  if (d?.capabilities.chat) endpoints.push(["chat", "/v1/chat/completions"]);
  if (d?.capabilities.img) endpoints.push(["img", "/v1/images/generations"]);
  if (d?.capabilities.vgen) endpoints.push(["vgen", "/v1/videos/generations"]);
  if (d?.capabilities.emb) endpoints.push(["emb", "/v1/embeddings"]);
  if (d?.capabilities.tr) endpoints.push(["tr", "/v1/audio/transcriptions"]);
  if (d?.capabilities.tts) endpoints.push(["tts", "/v1/audio/speech"]);

  const perLabel =
    basis === "per_second" ? "به‌ازای هر ثانیه" : basis === "per_image" ? "به‌ازای هر تصویر" : "به‌ازای ۱ میلیون توکن";
  // Image models billed per image still report real per-token prompt/completion
  // prices (the text part of an image request). When those differ from the
  // per-image price, the ورودی/خروجی rows hold per-token values and must be
  // labelled per token — the قيمت هر تصویر row keeps the per-image price.
  const ioPerLabel =
    basis === "per_image" &&
    d &&
    d.pricing.prompt != null &&
    d.pricing.image != null &&
    d.pricing.prompt !== d.pricing.image
      ? "به‌ازای هر توکن"
      : perLabel;
  const defaultParams = d?.default_parameters || {};

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" style={{ fontFamily: "'Vazirmatn', 'Inter', system-ui, sans-serif" }}>
      <div
        className="absolute inset-0 animate-admin-fade-in"
        onMouseDown={(e) => { backdropMouseDown.current = e.target === e.currentTarget; }}
        onClick={(e) => { if (e.target === e.currentTarget && backdropMouseDown.current) onClose(); backdropMouseDown.current = false; }}
        style={{ background: "rgba(17,24,39,0.35)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}
      />

      <aside className="admin-drawer-panel absolute top-0 bottom-0 left-0 w-full md:w-1/2 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="pt-5 pb-4 px-6" style={{ background: "var(--admin-bg-surface)", borderBottom: "1px solid var(--admin-border)" }}>
          <div className="flex items-start justify-between gap-4" dir="ltr">
            <div className="min-w-0 text-left">
              <div className="flex items-center gap-2 flex-wrap justify-end" dir="rtl">
                <span style={{ fontSize: 13, color: "var(--admin-text-tertiary)" }}>{d?.provider ? cap(d.provider) : ""}</span>
                {d?.is_free && (
                  <span className="admin-badge admin-badge-success" style={{ fontSize: 10 }}>رایگان</span>
                )}
              </div>
              <h2 className="truncate" dir="rtl" style={{ fontSize: 20, fontWeight: 700, color: "var(--admin-text-primary)", textAlign: "right" }}>
                {d?.display_name || (selectedModel.display_name as string) || "—"}
              </h2>
              <code
                className="block font-mono mt-1.5 tracking-tight"
                dir="ltr"
                style={{ fontSize: 12, color: "var(--admin-text-tertiary)", textAlign: "left" }}
              >
                {d?.model_name || (selectedModel.model_name as string)}
              </code>
            </div>
            <button
              onClick={onClose}
              aria-label="بستن"
              className="p-1 shrink-0 transition-colors"
              style={{ color: "var(--admin-text-tertiary)" }}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6" style={{ background: "var(--admin-bg-surface)" }}>
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" fill="none" style={{ color: "var(--admin-primary)" }}>
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          )}

          {isError && (
            <div className="flex items-center justify-center py-16">
              <p style={{ fontSize: 14, color: "var(--admin-text-tertiary)" }}>دریافت جزئیات مدل ناموفق بود</p>
            </div>
          )}

          {!isLoading && !isError && d && (
            <>
              {d.description && (
                <p dir="ltr" className="leading-relaxed text-left" style={{ fontSize: 13, color: "var(--admin-text-secondary)" }}>
                  {d.description}
                </p>
              )}

              <div>
                <SectionTitle>مشخصات</SectionTitle>
                <dl>
                  {d.created != null && (
                    <MetaRow label="تاریخ ایجاد">
                      {new Date(d.created * 1000).toLocaleDateString("fa-IR", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </MetaRow>
                  )}
                  <MetaRow label="معماری">
                    <span className="block" style={{ textAlign: "right" }}>
                      {modFa(d.input_modalities)} <span style={{ color: "var(--admin-text-tertiary)", margin: "0 2px" }}>←</span>{" "}
                      {modFa(d.output_modalities)}
                    </span>
                    <span className="block font-mono mt-1" dir="ltr" style={{ fontSize: 11, color: "var(--admin-text-tertiary)", textAlign: "left" }}>
                      {d.architecture_modality || "—"}
                    </span>
                  </MetaRow>
                  <MetaRow label="خروجی‌ها">
                    {d.output_modalities && d.output_modalities.length ? d.output_modalities.join(", ") : "—"}
                  </MetaRow>
                  {d.tokenizer && <MetaRow label="توکن‌ساز">{d.tokenizer}</MetaRow>}
                  <MetaRow label="حافظه (context)" info={INFO_TIPS.ctx}>
                    {fmtCtx(d.context_length)}
                  </MetaRow>
                  <MetaRow label="حداکثر توکن خروجی" info={INFO_TIPS.mct}>
                    {d.max_completion_tokens != null ? Number(d.max_completion_tokens).toLocaleString("en-US") : "—"}
                  </MetaRow>
                </dl>
              </div>

              <div>
                <SectionTitle>قابلیت‌های پشتیبانی‌شده</SectionTitle>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {CAP_LIST.map(([key, label]) => {
                    const on = !!d.capabilities[key as keyof ModelDetail["capabilities"]];
                    return on ? (
                      <span
                        key={key}
                        className="flex items-center gap-1.5 rounded-xl px-2.5 py-1.5"
                        style={{ fontSize: 12, color: "var(--admin-success)", background: "var(--admin-success-bg)", border: "1px solid var(--admin-border)" }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="shrink-0">
                          <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {label}
                      </span>
                    ) : (
                      <span
                        key={key}
                        className="flex items-center gap-1.5 rounded-xl px-2.5 py-1.5"
                        style={{ fontSize: 12, color: "var(--admin-text-tertiary)", background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)" }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="shrink-0">
                          <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                        </svg>
                        {label}
                      </span>
                    );
                  })}
                </div>
              </div>

              <div>
                <SectionTitle>پایان‌پوینت‌های پشتیبانی‌شده</SectionTitle>
                <div className="flex flex-wrap gap-2">
                  {endpoints.map(([, path]) => (
                    <code
                      key={path}
                      dir="ltr"
                      className="flex items-center gap-2 font-mono rounded-xl px-3 py-1.5"
                      style={{ fontSize: 11, color: "var(--admin-text-primary)", background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)" }}
                    >
                      <span
                        className="rounded px-1 py-0.5"
                        style={{ fontSize: 10, fontWeight: 500, color: "var(--admin-info)", background: "var(--admin-info-bg)" }}
                      >
                        POST
                      </span>
                      {path}
                    </code>
                  ))}
                </div>
              </div>

              <div>
                <SectionTitle>تعرفهٔ دقیق (دلاری)</SectionTitle>
                <div className="overflow-hidden divide-y" style={{ border: "1px solid var(--admin-border)", borderRadius: 14 }}>
                  <PricingRow label={`ورودی (${ioPerLabel})`} value={fmtUsd(inUsd)} />
                  <PricingRow label={`خروجی (${ioPerLabel})`} value={fmtUsd(outUsd)} />
                  {basis === "per_image" && d.pricing.image != null && (
                    <PricingRow label="قیمت هر تصویر" value={fmtUsd(margined(d.pricing.image))} />
                  )}
                  {d.pricing.request != null && (
                    <PricingRow label="هزینهٔ هر درخواست" value={fmtUsd(margined(d.pricing.request))} />
                  )}
                  {d.pricing.web_search != null && (
                    <PricingRow label="هزینهٔ هر جست‌وجوی وب" value={fmtUsd(margined(d.pricing.web_search))} />
                  )}
                  {d.generation?.pricing_skus &&
                    Object.keys(d.generation.pricing_skus).length > 0 &&
                    Object.entries(d.generation.pricing_skus).map(([sku, price]) => (
                      <PricingRow key={sku} label={sku} value={fmtUsd(margined(parseFloat(String(price)) * scale))} monoLabel ltr />
                    ))}
                </div>
              </div>

              {d.supported_parameters && d.supported_parameters.length > 0 && (
                <div>
                  <SectionTitle>پارامترهای قابل تنظیم</SectionTitle>
                  <ChipList items={d.supported_parameters} />
                </div>
              )}

              {(() => {
                const g = d.generation;
                const hasGen = g && (
                  (g.supported_resolutions?.length) ||
                  (g.supported_aspect_ratios?.length) ||
                  (g.supported_sizes?.length) ||
                  (g.supported_durations?.length) ||
                  (g.supported_frame_images?.length) ||
                  (g.allowed_passthrough_parameters?.length) ||
                  g.upscale_factor ||
                  g.creativity ||
                  g.generate_audio != null ||
                  g.seed != null ||
                  g.supports_streaming != null ||
                  (g.supported_parameters && Object.keys(g.supported_parameters).length > 0)
                );
                if (!hasGen) return null;
                return (
                  <div>
                    <SectionTitle>مشخصات تولید</SectionTitle>
                    <dl>
                      {g.supported_resolutions?.length ? (
                        <MetaRow label="رزلوشن‌های پشتیبانی‌شده"><ChipList items={g.supported_resolutions} /></MetaRow>
                      ) : null}
                      {g.supported_aspect_ratios?.length ? (
                        <MetaRow label="نسبت ابعاد پشتیبانی‌شده"><ChipList items={g.supported_aspect_ratios} /></MetaRow>
                      ) : null}
                      {g.supported_sizes?.length ? (
                        <MetaRow label="ابعاد پشتیبانی‌شده"><ChipList items={g.supported_sizes} /></MetaRow>
                      ) : null}
                      {g.supported_durations?.length ? (
                        <MetaRow label="مدت‌های پشتیبانی‌شده"><ChipList items={g.supported_durations.map((s) => String(s))} /></MetaRow>
                      ) : null}
                      {g.supported_frame_images?.length ? (
                        <MetaRow label="قاب‌های ورودی"><ChipList items={g.supported_frame_images} /></MetaRow>
                      ) : null}
                      {g.supported_parameters && Object.keys(g.supported_parameters).length > 0
                        ? Object.entries(g.supported_parameters).map(([name, spec]) => {
                            const label = GEN_PARAM_LABELS[name] || (
                              <span className="font-mono" dir="ltr">{name}</span>
                            );
                            const s: GenerationParamSpec | undefined = spec as GenerationParamSpec | undefined;
                            if (s && Array.isArray(s.values) && s.values.length) {
                              return (
                                <MetaRow key={name} label={label}><ChipList items={s.values.map((v) => String(v))} /></MetaRow>
                              );
                            }
                            if (s && (s.type === "range" || s.min != null || s.max != null)) {
                              return (
                                <MetaRow key={name} label={label}>
                                  {`${s.min ?? "—"} – ${s.max ?? "—"}`}
                                </MetaRow>
                              );
                            }
                            if (s && (s.type === "boolean" || typeof s.default === "boolean")) {
                              return (
                                <MetaRow key={name} label={label}>
                                  {YesNo(typeof s.default === "boolean" ? s.default : null)}
                                </MetaRow>
                              );
                            }
                            return null;
                          })
                        : null}
                      {g.upscale_factor ? (
                        <MetaRow label="فاکتور آپاسکیل">
                          {`${g.upscale_factor.min ?? "—"}x – ${g.upscale_factor.max ?? "—"}x`}
                        </MetaRow>
                      ) : null}
                      {g.creativity?.length ? (
                        <MetaRow label="محدوده خلاقیت">
                          {`${g.creativity[0]} – ${g.creativity[1] ?? g.creativity[0]}`}
                        </MetaRow>
                      ) : null}
                      {g.generate_audio != null && (
                        <MetaRow label="تولید صدا در خروجی">{YesNo(g.generate_audio)}</MetaRow>
                      )}
                      {g.seed != null && (
                        <MetaRow label="پشتیبانی از seed">{YesNo(g.seed)}</MetaRow>
                      )}
                      {g.supports_streaming != null && (
                        <MetaRow label="پشتیبانی از streaming">{YesNo(g.supports_streaming)}</MetaRow>
                      )}
                      {g.allowed_passthrough_parameters?.length ? (
                        <MetaRow label="پارامترهای عبور مجاز"><ChipList items={g.allowed_passthrough_parameters} /></MetaRow>
                      ) : null}
                    </dl>
                  </div>
                );
              })()}

              {Object.keys(defaultParams).length > 0 && (
                <div>
                  <SectionTitle>پارامترهای پیش‌فرض</SectionTitle>
                  <dl>
                    {Object.keys(defaultParams).map((k) => (
                      <MetaRow key={k} label={<span className="font-mono" dir="ltr">{k}</span>}>
                        {String(defaultParams[k])}
                      </MetaRow>
                    ))}
                  </dl>
                </div>
              )}

              {AdminBenchmarksBlock(d.benchmarks)}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function PricingRow({
  label,
  value,
  monoLabel = false,
  ltr = false,
}: {
  label: string;
  value: string;
  monoLabel?: boolean;
  ltr?: boolean;
}) {
  return (
    <div
      className="flex items-center justify-between px-4 py-2.5"
      style={{ background: "var(--admin-bg-surface)" }}
    >
      <span
        className={monoLabel ? "font-mono" : ""}
        dir={ltr ? "ltr" : undefined}
        style={{ fontSize: 12, color: "var(--admin-text-secondary)" }}
      >
        {label}
      </span>
      <span className="font-mono font-medium" dir="ltr" style={{ fontSize: 13, color: "var(--admin-text-primary)" }}>
        {value}
      </span>
    </div>
  );
}
